import React, { useEffect, useState } from "react";
import { TFunction, useTranslation } from "react-i18next";
import { groupGroupReportRenderArea, summaryGroupReportRenderArea } from "../../../../utils/constants";
import { ReportTableTableProps } from "../../../../utils/types";
import { ReactComponent as DownArrowSVG } from "../../../images/down-arrow.svg";
import { ReactComponent as UpArrowSVG } from "../../../images/up-arrow.svg";
import { SubReportApi } from "../../../services/types";
import {
	getAggregatePercentValue,
	getAggregateValue,
	renderAggregateComparePercentValue,
	renderAggregateValue,
	renderGroupByName,
} from "../../../services/utils";

const getShouldRenderCheckboxCustomField = (groupByType: string, allGroups?: Record<string, string>[]): boolean => {
	if (!groupByType.startsWith("+")) return false;

	const customFieldResourceTypeSeparatorIndex = groupByType.lastIndexOf(".");
	if (customFieldResourceTypeSeparatorIndex === -1) return false;

	const pureCustomFieldName = groupByType.substring(0, customFieldResourceTypeSeparatorIndex);
	const checkboxCustomField = allGroups?.find((group) => group.id === pureCustomFieldName && group.type === "radio");

	return checkboxCustomField !== undefined;
};

const getNameColumnContent = (
	t: TFunction,
	subItem: SubReportApi,
	allGroups?: Record<string, string>[],
	groupsInfo?: Record<string, any>
) => {
	const { group_by_type, group_by_value } = subItem;
	const shouldRenderGroupsInfoValue =
		groupsInfo && Object.prototype.hasOwnProperty.call(groupsInfo, `${group_by_type}`);

	if (shouldRenderGroupsInfoValue) return groupsInfo?.[`${group_by_type}`](group_by_value);

	let groupByValueName = group_by_value?.name;
	const shouldRenderCheckboxCustomField = getShouldRenderCheckboxCustomField(group_by_type, allGroups);

	if (shouldRenderCheckboxCustomField && groupByValueName !== "empty") {
		groupByValueName = t(`common.word.${groupByValueName === "true" ? "yes" : "no"}`, { ns: "lib" });
	}

	return renderGroupByName(groupByValueName);
};

const Table = (props: ReportTableTableProps): JSX.Element => {
	const { t } = useTranslation();
	const [renderAmount, setRenderAmount] = useState(30);

	useEffect(() => {
		handleResize();
	}, []);

	const handleScroll = (e: any) => {
		if (e.target.scrollHeight - e.target.scrollTop - e.target.clientHeight < 1) {
			setRenderAmount((renderAmount) => renderAmount + 20);
		}
	};

	const handleResize = () => {
		if (window.innerHeight > window.screen.availHeight) {
			setRenderAmount((renderAmount) => renderAmount + 20);
			setTimeout(() => {
				handleResize();
			}, 100);
		}
	};

	const buildLevel = (subItem: SubReportApi, mainAggregate: any, level: number, isSummaryRow?: boolean) => {
		const padding = (level - 1) * 30 + 10;
		const currentReportRenderArea = isSummaryRow ? summaryGroupReportRenderArea : groupGroupReportRenderArea;

		return (
			<tr>
				{subItem.group_by_type === "NONE" ? (
					<td>
						<strong>{t("lib:common.word.summary")}</strong>
					</td>
				) : (
					<td style={{ paddingLeft: `${padding}px` }}>
						{getNameColumnContent(t, subItem, props.groups, props.groupsInfo)}
					</td>
				)}
				{props.columns.map((col, index) => {
					let percentValue;
					if (level === 0) {
						percentValue = `${t("lib:go_report.word.percent_from_all")}`;
					} else {
						percentValue = getAggregatePercentValue(
							col,
							subItem,
							mainAggregate,
							currentReportRenderArea,
							props.aggregatePrefix
						);
					}
					const compareValue = renderAggregateComparePercentValue(
						col,
						subItem,
						currentReportRenderArea,
						props.aggregatePrefix
					);

					return (
						<td key={index}>
							{props.comparable ? (
								<>
									{level === 0 ? (
										<>
											{renderAggregateValue(
												col,
												subItem.aggregate,
												currentReportRenderArea,
												props.aggregatePrefix
											)}
											{Number(
												getAggregateValue(
													col,
													subItem.aggregate,
													currentReportRenderArea,
													props.aggregatePrefix
												)
											) >
												Number(
													getAggregateValue(
														col,
														subItem.compare_aggregate,
														currentReportRenderArea,
														props.aggregatePrefix
													)
												) && <UpArrowSVG className="arrow-icon" />}
											{Number(
												getAggregateValue(
													col,
													subItem.aggregate,
													currentReportRenderArea,
													props.aggregatePrefix
												)
											) <
												Number(
													getAggregateValue(
														col,
														subItem.compare_aggregate,
														currentReportRenderArea,
														props.aggregatePrefix
													)
												) && <DownArrowSVG className="arrow-icon" />}
											<small data-testid="report-table-compare-value">
												{" vs "}
												{renderAggregateValue(
													col,
													subItem.compare_aggregate,
													currentReportRenderArea,
													props.aggregatePrefix
												)}
											</small>
											{props.showPercent && (
												<strong className="d-block">
													{compareValue}
													{compareValue !== "-" ? "%" : ""}
												</strong>
											)}
										</>
									) : (
										<>
											{renderAggregateValue(
												col,
												subItem.aggregate,
												currentReportRenderArea,
												props.aggregatePrefix
											)}
											{Number(
												getAggregateValue(
													col,
													subItem.aggregate,
													currentReportRenderArea,
													props.aggregatePrefix
												)
											) >
												Number(
													getAggregateValue(
														col,
														subItem.compare_aggregate,
														currentReportRenderArea,
														props.aggregatePrefix
													)
												) && <UpArrowSVG className="arrow-icon" />}
											{Number(
												getAggregateValue(
													col,
													subItem.aggregate,
													currentReportRenderArea,
													props.aggregatePrefix
												)
											) <
												Number(
													getAggregateValue(
														col,
														subItem.compare_aggregate,
														currentReportRenderArea,
														props.aggregatePrefix
													)
												) && <DownArrowSVG className="arrow-icon" />}
											<small data-testid="report-table-compare-value">
												{" vs "}
												{renderAggregateValue(
													col,
													subItem.compare_aggregate,
													currentReportRenderArea,
													props.aggregatePrefix
												)}
											</small>
											{props.showPercent && (
												<strong className="d-block">
													{compareValue}
													{compareValue !== "-" ? "%" : ""}
												</strong>
											)}
										</>
									)}
								</>
							) : (
								<strong>
									{renderAggregateValue(
										col,
										subItem.aggregate,
										isSummaryRow ? summaryGroupReportRenderArea : groupGroupReportRenderArea,
										props.aggregatePrefix
									)}
									{props.showPercent && <small> {percentValue}</small>}
								</strong>
							)}
						</td>
					);
				})}
			</tr>
		);
	};

	useEffect(() => {
		window.addEventListener("scroll", handleScroll, true);
		window.addEventListener("resize", handleResize, true);
		return () => {
			window.removeEventListener("scroll", handleScroll, true);
			window.removeEventListener("resize", handleResize, true);
		};
	});
	let amount = 0;
	return (
		<tbody data-testid="report-table-body">
			{props.data.reports.map((subItem, index) => {
				const mainAggregate = subItem.aggregate[`${props.aggregatePrefix}`];
				++amount;
				if (amount > renderAmount) {
					return null;
				}
				return (
					<React.Fragment key={`aggr_${index}_${0}`}>
						{buildLevel(subItem, mainAggregate, 0, index === 0)}
						{subItem.sub_report?.map((subItem2, index) => {
							++amount;
							if (amount > renderAmount) {
								return null;
							}
							return (
								<React.Fragment key={`aggr_${index}_${1}`}>
									{buildLevel(subItem2, mainAggregate, 1)}
									{subItem2.sub_report?.map((subItem3, index) => {
										++amount;
										if (amount > renderAmount) {
											return null;
										}
										return (
											<React.Fragment key={`aggr_${index}_${2}`}>
												{buildLevel(subItem3, mainAggregate, 2)}
												{subItem3.sub_report?.map((subItem4, index) => {
													++amount;
													if (amount > renderAmount) {
														return null;
													}
													return (
														<React.Fragment key={`aggr_${index}_${3}`}>
															{buildLevel(subItem4, mainAggregate, 3)}
															{subItem4.sub_report?.map((subItem5, index) => {
																++amount;
																if (amount > renderAmount) {
																	return null;
																}
																return (
																	<React.Fragment key={`aggr_${index}_${4}`}>
																		{buildLevel(subItem5, mainAggregate, 4)}
																		{subItem5.sub_report?.map((subItem6, index) => {
																			++amount;
																			if (amount > renderAmount) {
																				return null;
																			}
																			return (
																				<React.Fragment
																					key={`aggr_${index}_${5}`}
																				>
																					{buildLevel(
																						subItem6,
																						mainAggregate,
																						5
																					)}
																					{subItem6.sub_report?.map(
																						(subItem7, index) => {
																							++amount;
																							if (amount > renderAmount) {
																								return null;
																							}
																							return (
																								<React.Fragment
																									key={`aggr_${index}_${6}`}
																								>
																									{buildLevel(
																										subItem7,
																										mainAggregate,
																										6
																									)}
																								</React.Fragment>
																							);
																						}
																					)}
																				</React.Fragment>
																			);
																		})}
																	</React.Fragment>
																);
															})}
														</React.Fragment>
													);
												})}
											</React.Fragment>
										);
									})}
								</React.Fragment>
							);
						})}
					</React.Fragment>
				);
			})}
		</tbody>
	);
};
export default Table;
